import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { CONTACT_EMAIL } from "@/lib/contact"
import type { AvailableTenant } from "@/hooks/use-auth"
import type { PlanResource, PlanResourceUsage, TenantPlan } from "@/types/api"

// Paramètres › Abonnement : prix publics, libellés des limites, demande de
// passage. Les plafonds viennent du serveur (`GET /settings/plan`), les prix
// de la page Tarifs du site (`website/src/data/pricing.json`).

/** Prix mensuels HT de la page Tarifs, en euros. */
export const PLAN_MONTHLY_PRICES: Readonly<Record<TenantPlan, number>> = {
  free: 0,
  solo: 9,
  equipe: 29,
  enterprise: 79,
}

/** Prix annuel HT : 10 mois facturés, comme sur la page Tarifs. */
export function annualPrice(monthly: number): number {
  return monthly * 10
}

/** Adresse qui reçoit les demandes de passage de plan. */
export const PLAN_REQUEST_EMAIL = CONTACT_EMAIL

const BYTES_PER_MB = 1024 * 1024

function plural(count: number, singular: string, pluralForm: string): string {
  return count >= 2 ? pluralForm : singular
}

/**
 * Taille lisible, en Mo puis en Go.
 *
 * @example
 * formatStorage(104857600) // "100 Mo"
 * formatStorage(10737418240) // "10 Go"
 */
export function formatStorage(bytes: number): string {
  const mb = bytes / BYTES_PER_MB
  const format = (value: number) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(value)
  return mb >= 1024 ? `${format(mb / 1024)} Go` : `${format(mb)} Mo`
}

/**
 * Consommation d'une limite, telle que l'onglet l'affiche.
 *
 * @example
 * usageText({ resource: "program", used: 2, limit: 3 }) // "2 programmes sur 3"
 * usageText({ resource: "session_per_month", used: 0, limit: 1 }) // "0 session sur 1 ce mois-ci"
 */
export function usageText({ resource, used, limit }: PlanResourceUsage): string {
  const bounded = (unit: string, suffix: string) =>
    limit === null ? `${used} ${unit}${suffix}, sans limite` : `${used} ${unit} sur ${limit}${suffix}`
  switch (resource) {
    case "program":
      return bounded(plural(used, "programme", "programmes"), "")
    case "session_per_month":
      return bounded(plural(used, "session", "sessions"), " ce mois-ci")
    case "learner_per_year":
      return bounded(plural(used, "apprenant", "apprenants"), " cette année")
    case "user":
      return bounded(plural(used, "utilisateur", "utilisateurs"), "")
    case "storage_bytes":
      return limit === null
        ? `${formatStorage(used)} utilisés, sans limite`
        : `${formatStorage(used)} sur ${formatStorage(limit)}`
    case "ai_import_per_month":
      return limit === 0 ? "Non inclus dans votre plan" : bounded(plural(used, "import", "imports"), " ce mois-ci")
  }
}

/** Nom d'une limite, en tête de sa ligne de consommation. */
export const RESOURCE_LABELS: Readonly<Record<PlanResource, string>> = {
  program: "Programmes",
  session_per_month: "Sessions de formation",
  learner_per_year: "Apprenants",
  user: "Utilisateurs",
  storage_bytes: "Stockage",
  ai_import_per_month: "Import IA de programme",
}

/**
 * Plafond d'un plan, dans la grille.
 *
 * @example
 * offerLimitText("program", 3) // "Jusqu'à 3 programmes"
 * offerLimitText("program", null) // "Programmes illimités"
 */
export function offerLimitText(resource: PlanResource, limit: number | null): string {
  switch (resource) {
    case "program":
      return limit === null ? "Programmes illimités" : `Jusqu'à ${limit} ${plural(limit, "programme", "programmes")}`
    case "session_per_month":
      return limit === null
        ? "Sessions de formation illimitées"
        : `${limit} ${plural(limit, "session", "sessions")} de formation par mois`
    case "learner_per_year":
      return limit === null ? "Apprenants illimités" : `${limit} ${plural(limit, "apprenant", "apprenants")} par an`
    case "user":
      if (limit === null) return "Utilisateurs illimités"
      return limit === 1 ? "1 utilisateur" : `Jusqu'à ${limit} utilisateurs`
    case "storage_bytes":
      return limit === null ? "Stockage illimité" : `${formatStorage(limit)} de stockage`
    case "ai_import_per_month":
      if (limit === null) return "Imports IA de programme illimités"
      return limit === 0
        ? "Sans import IA de programme"
        : `${limit} ${plural(limit, "import", "imports")} IA de programme par mois`
  }
}

/** Part consommée d'une limite, entre 0 et 1. `null` : rien à jauger. */
export function usageRatio({ used, limit }: PlanResourceUsage): number | null {
  if (limit === null || limit === 0) return null
  return Math.min(1, Math.max(0, used / limit))
}

/**
 * Lien `mailto:` de demande de passage à un plan, pré-rempli.
 *
 * @example
 * planRequestMailto("Équipe", "Atelier Formation", "atelier")
 * // "mailto:<adresse de contact>?subject=Passage%20au%20plan%20%C3%89quipe&body=…"
 */
export function planRequestMailto(planName: string, organisationName: string, slug: string): string {
  const subject = `Passage au plan ${planName}`
  const body = [
    "Bonjour,",
    "",
    `Nous souhaitons passer au plan ${planName} pour l'organisme ${organisationName} (identifiant : ${slug}).`,
    "",
    "Merci de nous indiquer la marche à suivre.",
  ].join("\n")
  return `mailto:${PLAN_REQUEST_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/**
 * Nom de l'organisme d'identifiant `slug`, ou l'identifiant à défaut.
 *
 * @example
 * organisationNameOf([{ slug: "atelier", name: "Atelier Formation" }], "atelier") // "Atelier Formation"
 */
export function organisationNameOf(tenants: readonly Pick<AvailableTenant, "slug" | "name">[], slug: string): string {
  return pipe(
    O.fromNullable(tenants.find((tenant) => tenant.slug === slug)),
    O.map((tenant) => tenant.name),
    O.getOrElse(() => slug),
  )
}
